import type { RawMqttMessage } from '@/lib/mqtt/types';
import { useDataStreamStore } from '@/lib/store/dataStream';
import { useEffect, useRef, useState } from 'react';
import { classifyTopic } from '../utils/classifyTopic';
import { extractMapObjects } from '../utils/extractMapObjects';
import {
  type LiveObject,
  type LiveStore,
  sweepObjects,
  upsertObjects,
} from '../utils/objectAccumulator';

const SWEEP_MS = 1_000;

interface Options {
  /** Only fold messages the active search/chips accept, so the map narrows to
   *  the same set the message list shows. */
  accept?: (msg: RawMqttMessage) => boolean;
  /** Identity of the predicate. When it changes the accumulator is rebuilt
   *  from the whole buffer — a narrower filter must be able to *remove*
   *  objects an earlier fold already admitted. */
  filterKey?: string;
}

/** Live objects for the selected topic: folds the topic buffer incrementally
 *  (newest-first, down to the last id we processed) and sweeps for staleness
 *  every second. Returns the survivors annotated with staleState. */
export function useTopicObjects(topic: string | null, options: Options = {}): LiveObject[] {
  const { accept, filterKey = '' } = options;
  const buffers = useDataStreamStore((s) => s.buffers);
  const [objects, setObjects] = useState<LiveObject[]>([]);
  const storeRef = useRef<LiveStore>(new Map());
  const lastIdRef = useRef<string | null>(null);
  // Read through a ref: the predicate's identity changes every render, but
  // only `filterKey` should drive a rebuild.
  const acceptRef = useRef(accept);
  acceptRef.current = accept;

  // Reset when the topic or the filter changes.
  // biome-ignore lint/correctness/useExhaustiveDependencies: `filterKey` is not read in the body — it is the predicate's identity, and a change to it must invalidate the accumulated fold.
  useEffect(() => {
    storeRef.current = new Map();
    lastIdRef.current = null;
    setObjects([]);
  }, [topic, filterKey]);

  // Fold new messages on each buffer change.
  // biome-ignore lint/correctness/useExhaustiveDependencies: same as above — `filterKey` re-runs the fold after the reset effect has cleared the watermark, so a narrowed filter drops objects an earlier fold admitted.
  useEffect(() => {
    if (!topic) return;
    const msgs = buffers.get(topic);
    if (!msgs || msgs.length === 0) return;
    const badge = classifyTopic(topic);
    const now = Date.now();
    const fresh: typeof msgs = [];
    for (const m of msgs) {
      // newest-first
      if (m.id === lastIdRef.current) break;
      fresh.push(m);
    }
    // Watermark tracks the buffer, not the filtered subset, so a rejected
    // newest message can't make us re-scan it on every commit.
    lastIdRef.current = msgs[0]!.id;
    const keep = acceptRef.current;
    // Apply oldest→newest so the newest position wins per id.
    for (let i = fresh.length - 1; i >= 0; i--) {
      const msg = fresh[i]!;
      if (keep && !keep(msg)) continue;
      upsertObjects(storeRef.current, extractMapObjects(msg.payload, badge, topic), now);
    }
    setObjects(sweepObjects(storeRef.current, now));
  }, [buffers, topic, filterKey]);

  // 1s staleness sweep.
  useEffect(() => {
    const t = window.setInterval(() => {
      setObjects(sweepObjects(storeRef.current, Date.now()));
    }, SWEEP_MS);
    return () => window.clearInterval(t);
  }, []);

  return objects;
}
